import { Dialog } from '@base-ui/react'
import { useCallback, useMemo } from 'react'
import { z } from 'zod'
import { TruncatedText } from '@/components/commons/truncated-text'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { DataTable } from '@/components/ui/data-table'
import { DialogTrigger } from '@/components/ui/dialog'
import {
	AlertTriangleIcon,
	CheckIcon,
	SearchLgIcon
} from '@/components/ui/icons'
import { Input } from '@/components/ui/input'
import { formatDate } from '@/dates'
import { useAuthStore } from '@/features/auth/store/store'
import { EmptyStateListings } from '@/features/luxe-submissions/components/empty-state-listings'
import { FilterLocationSelect } from '@/features/luxe-submissions/components/filter-location-select'
import { FilterVetterSelect } from '@/features/luxe-submissions/components/filter-vetter-select'
import { FinalScoreBadge } from '@/features/luxe-submissions/components/final-score-badge'
import { GenerateQuoteDialog } from '@/features/luxe-submissions/components/generate-quote-dialog'
import { ReviewerNameLabel } from '@/features/luxe-submissions/components/reviewer-name-label'
import { SourceBadge } from '@/features/luxe-submissions/components/source-badge'
import { SourceFilterSelect } from '@/features/luxe-submissions/components/source-filter-select'
import { SubmissionTypeFilterSelect } from '@/features/luxe-submissions/components/submission-type-filter-select'
import { useLuxeFeatureFlag } from '@/features/luxe-submissions/queries/use-get-luxe-feature-flags'
import { useGetQuoteGenerationListings } from '@/features/luxe-submissions/queries/use-get-quote-generation-listings'
import {
	type QuoteGenerationListing,
	SubmissionSource,
	SubmissionType
} from '@/features/luxe-submissions/types'
import {
	getQuoteGenerationBlockerLabelTKey,
	resolveQuoteGenerationState,
	submissionTypeToLabelTKey,
	toArrayParam
} from '@/features/luxe-submissions/utils'
import type { Order, OrderDirection } from '@/helpers/types'
import { cn, debounce } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import {
	ROW_SELECTION_COLUMN_ID,
	useRowSelection
} from '@/hooks/use-row-selection'
import { useRowsPerPage } from '@/hooks/use-rows-per-page'
import { useTranslation } from '@/translations/i18n'

const QuoteGenerationSearchParamsSchema = z.object({
	sortBy: z.string().optional(),
	sortOrder: z.enum(['asc', 'desc']).optional(),
	locationId: z.string().optional(),
	finalReviewerIds: z.string().optional(),
	source: SubmissionSource.optional(),
	submissionType: SubmissionType.optional(),
	search: z.string().optional(),
	page: z.string().optional(),
	perPage: z.string().optional()
})

const MM_REPORTING_SOURCES: SubmissionSource[] = ['marketing_manager']
type SortColumn =
	| 'listingId'
	| 'location'
	| 'submissionType'
	| 'source'
	| 'score'
	| 'finalReviewer'

const generateQuoteHandle = Dialog.createHandle<{
	listingIds: string[]
}>()

function LuxeSubmissionsQuoteGeneration() {
	const { t } = useTranslation()
	const user = useAuthStore((state) => state.user)
	const isLuxeOps = user?.role === 'luxe_ops'
	const { searchParams, setSearchParams } = useParsedSearchParams(
		QuoteGenerationSearchParamsSchema
	)
	const currentPage = searchParams.page ? Number(searchParams.page) : 1

	const { isEnabled: isFinalScoreEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-final-score-display'
	)
	const { isEnabled: isBlockedStateEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-quote-blocked-state'
	)
	const { isEnabled: isGeneratedStateEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-quote-generated-state'
	)
	// With the flag on the backend resolves this field to the visual vetter who
	// authored the review, and only to the Final Review decision maker when the
	// listing was escalated — so the column covers both roles.
	const { isEnabled: isFinalReviewerColumnEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-final-reviewer-column'
	)

	const getQuoteState = useCallback(
		(listing: QuoteGenerationListing) =>
			resolveQuoteGenerationState({
				state: listing.quoteGenerationState,
				isBlockedStateEnabled,
				isGeneratedStateEnabled
			}),
		[isBlockedStateEnabled, isGeneratedStateEnabled]
	)

	const reviewerColumnLabel = t(
		isFinalReviewerColumnEnabled
			? 'luxe_submissions:final_reviewer_vetter_column'
			: 'luxe_submissions:final_reviewer_column'
	)

	const { rowsPerPage, setRowsPerPage, totalRows, reportTotal } =
		useRowsPerPage({
			searchParam: searchParams.perPage,
			setSearchParam: (newPerPage) =>
				setSearchParams({ perPage: newPerPage, page: undefined })
		})

	// Queries
	const { data: listingsData, isLoading: isLoadingListings } =
		useGetQuoteGenerationListings({
			page: currentPage,
			perPage: rowsPerPage,
			sortBy: searchParams.sortBy,
			sortOrder: searchParams.sortOrder,
			locations: toArrayParam(searchParams.locationId),
			finalReviewerIds: toArrayParam(searchParams.finalReviewerIds),
			sources: toArrayParam(searchParams.source),
			submissionTypes: toArrayParam(searchParams.submissionType),
			search: searchParams.search
		})

	// Row selection - only pending rows are selectable
	const { rowSelectionColumn, selectionCount, selected, resetSelection } =
		useRowSelection<QuoteGenerationListing, 'id'>({
			idField: 'id',
			data: listingsData?.data,
			isSelectable: (listing) =>
				listing.quoteStatus === 'pending_quote' &&
				// Blocked is a hold and an external quote is already produced —
				// both must stay out of every generate action
				getQuoteState(listing) === 'ready' &&
				listing.canGenerateQuote !== false &&
				!isLuxeOps &&
				// the listings that were submitted by MM's have the cta generate quote disabled
				!MM_REPORTING_SOURCES.includes(listing.source),
			getDisabledReason: (listing) => {
				const quoteState = getQuoteState(listing)
				if (quoteState === 'blocked') {
					return (
						listing.quoteBlockReason ??
						t(
							'luxe_submissions:quote_generation_error.blocked_by_salesforce'
						)
					)
				}
				if (quoteState === 'quote_generated') {
					return t(
						'luxe_submissions:quote_generation_error.quote_generated_outside_luminate'
					)
				}
				if (listing.quoteStatus !== 'pending_quote') {
					return t(
						'luxe_submissions:quote_already_generated_select_disabled_reason'
					)
				}
				if (listing.canGenerateQuote === false) {
					return t(
						getQuoteGenerationBlockerLabelTKey(
							listing.quoteGenerationBlocker
						)
					)
				}
				if (MM_REPORTING_SOURCES.includes(listing.source)) {
					return t(
						'luxe_submissions:mm_source_quote_select_disabled_reason'
					)
				}
				if (isLuxeOps) {
					return t(
						'luxe_submissions:not_allowed_generate_quote_select_disabled_reason'
					)
				}
				return undefined
			}
		})

	// Pagination
	const lastPage = listingsData ? listingsData.meta.lastPage : undefined
	reportTotal(listingsData?.meta.total)

	const setPage = (page: number) => {
		setSearchParams({
			page: page === 1 ? undefined : String(page)
		})
	}

	// Filters
	const handleLocationChange = (locationId: string | null) => {
		setSearchParams({
			locationId: locationId ?? undefined,
			page: undefined
		})
	}

	const handleFinalReviewerChange = (finalReviewerId: string | null) => {
		setSearchParams({
			finalReviewerIds: finalReviewerId ?? undefined,
			page: undefined
		})
	}

	const handleSourceChange = (source: string | null) => {
		setSearchParams({
			source: (source as SubmissionSource | null) ?? undefined,
			page: undefined
		})
	}

	const handleSubmissionTypeChange = (submissionType: string | null) => {
		setSearchParams({
			submissionType: (submissionType as SubmissionType) ?? undefined,
			page: undefined
		})
	}

	// Debounced search by ID and email
	const debouncedSearch = useMemo(
		() =>
			debounce((value: string) => {
				setSearchParams({
					search: value || undefined,
					page: undefined
				})
			}, 300),
		[setSearchParams]
	)

	// Order state for DataTable
	const order = useMemo((): Order<SortColumn> | undefined => {
		if (!searchParams.sortBy) {
			return undefined
		}
		return {
			orderBy: searchParams.sortBy as SortColumn,
			orderDirection: searchParams.sortOrder as OrderDirection | undefined
		}
	}, [searchParams.sortBy, searchParams.sortOrder])

	const setOrder = (newOrder?: Order<SortColumn>) => {
		setSearchParams({
			sortBy: newOrder?.orderBy,
			sortOrder: newOrder?.orderDirection,
			page: undefined
		})
	}
	const hasFilters = Object.values(searchParams).some(Boolean)

	// Table columns
	const columns = useMemo(
		() => [
			rowSelectionColumn,
			{
				id: 'listingId' as const,
				header: t('luxe_submissions:listing_id_column'),
				cell: ({ dataItem }: { dataItem: QuoteGenerationListing }) => (
					<div className='flex flex-col'>
						<a
							className={cn(
								'truncate font-medium text-neutral-900 text-sm',
								dataItem.listingUrl && 'underline'
							)}
							href={dataItem.listingUrl ?? undefined}
							rel='noopener noreferrer'
							target='_blank'
						>
							<TruncatedText>
								{`#${dataItem.airbnbListingId ?? dataItem.id}`}
							</TruncatedText>
						</a>

						{dataItem.isEscalated && (
							<span className='text-danger-600 text-xs'>
								{t('luxe_submissions:escalated')}
							</span>
						)}
					</div>
				),
				size: 240,
				sortKey: 'listingId' as const
			},
			{
				id: 'location' as const,
				header: t('luxe_submissions:location_column'),
				cell: ({ dataItem }: { dataItem: QuoteGenerationListing }) => {
					return dataItem.location ? (
						<span className='text-neutral-700 text-sm'>
							{dataItem.location}
						</span>
					) : (
						<span className='text-neutral-500 text-sm'>—</span>
					)
				},
				size: 160,
				sortKey: 'location' as const
			},
			{
				id: 'submissionType' as const,
				header: t('luxe_submissions:submission_type_column'),
				cell: ({ dataItem }: { dataItem: QuoteGenerationListing }) =>
					dataItem.submissionType ? (
						<span className='text-neutral-700 text-sm'>
							{t(
								submissionTypeToLabelTKey[
									dataItem.submissionType
								]
							)}
						</span>
					) : (
						<span className='text-neutral-500 text-sm'>—</span>
					),
				size: 210,
				sortKey: 'submissionType' as const
			},
			{
				id: 'submittedAt' as const,
				header: t('luxe_submissions:submitted_at_column'),
				cell: ({ dataItem }: { dataItem: QuoteGenerationListing }) => {
					return dataItem.queueEnteredAt === null ? (
						<span className='text-neutral-500 text-sm'>—</span>
					) : (
						<span className='text-neutral-700 text-sm'>
							{formatDate(dataItem.queueEnteredAt, 'PP')}
						</span>
					)
				},
				size: 165
			},
			{
				id: 'source' as const,
				header: t('luxe_submissions:source_column'),
				cell: ({ dataItem }: { dataItem: QuoteGenerationListing }) => (
					<SourceBadge source={dataItem.source} />
				),
				size: 165,
				sortKey: 'source' as const
			},
			{
				id: 'finalReviewer' as const,
				header: reviewerColumnLabel,
				cell: ({ dataItem }: { dataItem: QuoteGenerationListing }) => {
					if (dataItem.finalReviewer) {
						return (
							<ReviewerNameLabel
								email=''
								firstName={dataItem.finalReviewer.firstName}
								lastName={dataItem.finalReviewer.lastName}
							/>
						)
					}
					return <span className='text-neutral-500 text-sm'>—</span>
				},
				size: 220,
				sortKey: 'finalReviewer' as const
			},
			{
				id: 'score' as const,
				header: t(
					isFinalScoreEnabled
						? 'luxe_submissions:final_score_column'
						: 'luxe_submissions:vv_score_column'
				),
				cell: ({ dataItem }: { dataItem: QuoteGenerationListing }) => (
					<FinalScoreBadge
						blockersFailedCount={dataItem.blockersFailedCount}
						designScore={dataItem.score}
						finalScore={dataItem.finalScore}
					/>
				),
				size: 160,
				sortKey: 'score' as const
			},
			{
				id: 'action' as const,
				header: '',
				cell: ({ dataItem }: { dataItem: QuoteGenerationListing }) => {
					const quoteState = getQuoteState(dataItem)

					const blockReason =
						dataItem.quoteBlockReason ??
						t('luxe_submissions:quote_blocked')
					// The backend can't quote this row (e.g. missing listing
					// info), so the CTA is replaced by the reason.
					const unavailableContent =
						dataItem.canGenerateQuote === false ? (
							<div className='flex min-w-0 flex-col items-end text-right'>
								<span className='truncate font-medium text-neutral-500 text-sm'>
									{t(
										'luxe_submissions:quote_generation_not_available'
									)}
								</span>
								<TruncatedText className='text-neutral-500 text-sm'>
									{t(
										getQuoteGenerationBlockerLabelTKey(
											dataItem.quoteGenerationBlocker
										)
									)}
								</TruncatedText>
							</div>
						) : null

					// A row that cannot be quoted shows why instead of the CTA.
					// The badge is the exclusion the brief asks for — the reason
					// needs this cell, and a disabled button would take it while
					// saying less. Which means the CTA below is only ever reached
					// by a ready row, and no ordering of the checks can leak it.
					const blockedContent = {
						blocked: (
							<span
								className='inline-flex items-center gap-1 rounded-full border border-warning-200 bg-warning-50 px-2 py-0.5 font-medium text-warning-700 text-xs'
								title={blockReason}
							>
								<AlertTriangleIcon className='size-3 shrink-0' />
								<span className='truncate'>{blockReason}</span>
							</span>
						),
						quote_generated: (
							<span className='rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 font-medium text-neutral-700 text-xs'>
								{t('luxe_submissions:quote_generated_outside')}
							</span>
						),
						// Quoted inside Luminate — tracked by quoteStatus, not by
						// the Salesforce state.
						ready:
							dataItem.quoteStatus === 'quote_generated' ? (
								<span className='inline-flex items-center gap-0.5 rounded-full border border-success-200 bg-success-50 px-2 py-0.5 font-medium text-success-700 text-xs'>
									<CheckIcon className='size-3 fill-success-500' />
									{t('luxe_submissions:quote_generated')}
								</span>
							) : (
								unavailableContent
							)
					}[quoteState]

					if (blockedContent) {
						return (
							<div className='inline-flex w-full items-center justify-end'>
								{blockedContent}
							</div>
						)
					}

					return (
						<DialogTrigger
							handle={generateQuoteHandle}
							nativeButton={true}
							payload={{
								listingIds: [dataItem.id]
							}}
							render={
								<ButtonLegacy
									className='h-auto w-full justify-end p-0 text-sm'
									disabled={
										selected.includes(dataItem.id) ||
										MM_REPORTING_SOURCES.includes(
											dataItem.source
										) ||
										isLuxeOps
									}
									type='button'
									variant='tertiary'
								>
									{t(
										'luxe_submissions:generate_quote_walkthrough'
									)}
								</ButtonLegacy>
							}
						/>
					)
				},
				size: 250
			}
		],
		[
			t,
			rowSelectionColumn,
			selected,
			isLuxeOps,
			isFinalScoreEnabled,
			getQuoteState,
			reviewerColumnLabel
		]
	)
	return (
		<div className='flex flex-1 flex-col overflow-hidden px-10'>
			{/* Filters */}
			<div className='flex shrink-0 items-center justify-between gap-3 py-6'>
				<div className='flex min-w-0 items-center gap-3'>
					<FilterLocationSelect
						onChange={handleLocationChange}
						selectedId={searchParams.locationId ?? null}
					/>
					<SubmissionTypeFilterSelect
						onChange={handleSubmissionTypeChange}
						value={searchParams.submissionType ?? null}
					/>
					<SourceFilterSelect
						onChange={handleSourceChange}
						value={searchParams.source ?? null}
					/>
					<FilterVetterSelect
						onChange={handleFinalReviewerChange}
						placeholder={reviewerColumnLabel}
						section='final_review'
						selectedId={searchParams.finalReviewerIds ?? null}
						userId={user?.id}
					/>
				</div>
				<div className='relative w-[320px]'>
					<SearchLgIcon
						className='pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-neutral-500'
						size={20}
					/>
					<Input
						className='h-9 w-full rounded-lg border-neutral-300 pl-10 text-sm shadow-xs placeholder:text-neutral-500'
						defaultValue={searchParams.search ?? ''}
						onChange={(e) => debouncedSearch(e.target.value)}
						placeholder={t(
							'luxe_submissions:search_by_listing_id_email'
						)}
					/>
				</div>
			</div>

			{/* Selection bar */}
			{selectionCount > 0 && (
				<div className='mb-4 flex items-center justify-between rounded-lg border border-neutral-200 bg-primary-50 px-6 py-3'>
					<span className='font-medium text-neutral-600 text-xs'>
						{selectionCount} Selected
					</span>
					<div className='flex items-center gap-2'>
						<DialogTrigger
							handle={generateQuoteHandle}
							nativeButton={true}
							payload={{
								listingIds: selected
							}}
							render={
								<ButtonLegacy
									className='h-auto px-3 py-1.5 text-sm'
									type='button'
									variant='outline'
								>
									{t('luxe_submissions:generate_quote')}
								</ButtonLegacy>
							}
						/>
						<ButtonLegacy
							onClick={resetSelection}
							type='button'
							variant='tertiary'
						>
							{t('common:clear')}
						</ButtonLegacy>
					</div>
				</div>
			)}

			{/* Table */}
			<div className='flex-1 overflow-hidden'>
				<DataTable
					columnPinning={{
						left: [ROW_SELECTION_COLUMN_ID],
						right: ['action']
					}}
					columns={columns}
					data={listingsData?.data ?? []}
					emptyComponent={
						<EmptyStateListings
							action={
								hasFilters ? null : (
									<ButtonLegacy
										onClick={() => window.location.reload()}
										variant='secondary'
									>
										{t('common:refresh')}
									</ButtonLegacy>
								)
							}
							description={t(
								hasFilters
									? 'common:empty_table_description'
									: 'luxe_submissions:quote_generation_no_listings_description'
							)}
							title={t(
								hasFilters
									? 'common:empty_table_title'
									: 'luxe_submissions:quote_generation_no_listings_title'
							)}
						/>
					}
					isLoading={isLoadingListings}
					lastPage={lastPage}
					order={order}
					page={currentPage}
					rowsPerPage={rowsPerPage}
					setOrder={setOrder}
					setPage={setPage}
					setRowsPerPage={setRowsPerPage}
					totalRows={totalRows}
				/>
			</div>

			<GenerateQuoteDialog
				handle={generateQuoteHandle}
				onSuccess={selectionCount > 0 ? resetSelection : undefined}
			/>
		</div>
	)
}

export { LuxeSubmissionsQuoteGeneration }
